import { scaleLog } from 'd3-scale';
import { GraphProps } from './graph-single';

export function computeDomain(
  direction: 'start' | 'end',
  data: { time: Date }[],
  zoomRange: [Date, Date],
  dataKeys: string[],
) {
  const [start, end] = zoomRange;
  const dataPoints = data
    .filter((d) => d.time >= start && d.time <= end)
    .flatMap((r: any) => dataKeys.map((k) => r[k]))
    .filter((r: any) => r !== null);

  const min = Math.min(...dataPoints);
  const max = Math.max(...dataPoints);

  if (direction === 'start') {
    // Minus a small margin..
    const with_margin = Math.round((min - (max - min) * 0.25) * 100) / 100;
    return min >= 0 && with_margin < 0 ? 0 : with_margin;
  } else {
    // Plus a small margin..
    return Math.round((max + (max - min) * 0.15) * 100) / 100;
  }
}

export function getDataPointsCountWithinRange(data: { time: Date }[], zoomRange: [Date, Date]) {
  const [start, end] = zoomRange;
  return data.filter((d) => d.time >= start && d.time <= end).length;
}

export function markerSize(
  data: Record<string, any>[],
  zoomRange: [Date, Date],
  maximumDataPoints?: number,
) {
  const dataLength = zoomRange
    ? maximumDataPoints || getDataPointsCountWithinRange(data as any, zoomRange)
    : data.length;

  const range = [7, 2];
  if (dataLength < 20) return 10;
  const scale = scaleLog().domain([100, 2500]).range(range);
  return Math.max(scale(dataLength), 2);
}

// We need this utility function to check if all data is empty when gapfill is enabled
// See: https://docs.timescale.com/api/latest/hyperfunctions/gapfilling-interpolation/time_bucket_gapfill/l
export function isAllDataEmpty(xAxisKey: string, data: NonNullable<GraphProps['data']>) {
  for (const record of data) {
    for (const [k, v] of Object.entries(record)) {
      if (k !== xAxisKey && v !== null) {
        return false;
      }
    }
  }

  return true;
}

export function groupByKey<T extends Record<string, any>, K extends keyof T>(data: T[], key: K) {
  return groupBy(data, (r) => r[key]);
}

export function groupBy<T extends Record<string, any>>(
  data: T[],
  selector: (t: T) => string | number,
) {
  const grouped = {} as Record<string | number, T[]>;

  for (const record of data) {
    const groupKey = selector(record);
    const records = grouped[groupKey];
    if (records) {
      records.push(record);
    } else {
      grouped[groupKey] = [record];
    }
  }

  return grouped;
}
